import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import EFilterButton from '@/components/everridge/EFilterButton';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { FilterHelper } from '@/utils/filterHelper';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminDataPageFilter from '@/features/superAdmin/features/data/shared/components/SuperAdminDataPageFilter';
import SuperAdminExpandableDataTable from '@/features/superAdmin/features/data/shared/components/SuperAdminExpandableDataTable';
import SuperAdminDataNestedTable from '@/features/superAdmin/features/data/shared/components/SuperAdminDataNestedTable';
import {
    orderLineNestedColumns,
    useSuperAdminOrdersPageColumns,
} from '@/features/superAdmin/features/data/orders/components/SuperAdminOrdersPageColumns';
import { useSuperAdminOrdersFilter } from '@/features/superAdmin/features/data/orders/hooks/useSuperAdminOrdersFilter';
import { useSuperAdminOrdersListApi } from '@/features/superAdmin/features/data/orders/hooks/useSuperAdminOrdersListApi';

const SuperAdminOrdersListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const columns = useSuperAdminOrdersPageColumns();
    const { filter, setFilter, resetFilter } = useSuperAdminOrdersFilter();
    const [page, setPage] = useState(() => Math.floor(filter.skip / filter.take));
    const [showFilter, setShowFilter] = useState(false);
    const { list, isLoading } = useSuperAdminOrdersListApi({ suspendFetching: showFilter });

    useEffect(() => {
        const totalCount = list?.totalCount ?? 0;
        if ((totalCount === 0 && filter.skip > 0) || (totalCount > 0 && filter.skip >= totalCount)) {
            setFilter({ skip: 0 });
            setPage(0);
        }
    }, [list?.totalCount, filter.skip, setFilter]);

    useEffect(() => {
        setPage(Math.floor(filter.skip / filter.take));
    }, [filter.skip, filter.take]);

    if (userRole !== Roles.superAdmin) return null;

    const activeFiltersCount = FilterHelper.countFilterValues(filter, ['take', 'skip', 'orderBy', 'orderDirection', 'documentNumber']);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({ skip: filter.take * newPage });
    };

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={t('superAdmin.data.orders.title')}>
                <Button variant="outline" className="gap-2" onClick={() => navigate('/superAdmin/data')}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.backToHub')}
                </Button>
                <EFilterButton
                    name={t('elements.components.filter.title')}
                    filtersQuantity={activeFiltersCount}
                    onClick={() => setShowFilter(true)}
                />
            </EPageHeader>

            <SuperAdminExpandableDataTable
                columns={columns}
                data={list}
                page={page}
                setPage={handlePageChange}
                paginationStep={filter.take}
                isLoadingPage={isLoading}
                getRowId={(row) => row.id}
                renderExpandedContent={(row) => (
                    <SuperAdminDataNestedTable
                        title={t('superAdmin.data.nested.lines')}
                        columns={orderLineNestedColumns}
                        data={row.lines}
                    />
                )}
            />

            {showFilter && (
                <SuperAdminDataPageFilter
                    onClose={() => setShowFilter(false)}
                    isMobile={isMobile}
                    filter={filter}
                    setFilter={setFilter}
                    resetFilter={resetFilter}
                />
            )}
        </section>
    );
};

export default SuperAdminOrdersListPage;
